<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { Download, RefreshCw, X } from "lucide-vue-next";
import { useRegisterSW } from "virtual:pwa-register/vue";
import { useBrandStore } from "@/stores/brand";

const { t } = useI18n();
const brandStore = useBrandStore();
const { appName, loaded: brandLoaded } = storeToRefs(brandStore);

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
const showInstall = ref(false);
const dismissedInstall = ref(
  localStorage.getItem("pwa-install-dismissed") === "1"
);

/** Comprobar actualizaciones del SW mientras la SPA sigue abierta. */
const UPDATE_CHECK_MS = 60_000;
let updateIntervalId: number | undefined;
let registrationRef: ServiceWorkerRegistration | null = null;
let refreshing = false;

function checkForSwUpdate() {
  void registrationRef?.update();
}

function onVisibilityChange() {
  if (document.visibilityState === "visible") {
    checkForSwUpdate();
  }
}

/** Por si el evento `activated` de workbox no recarga (fallback). */
function onControllerChange() {
  if (refreshing) return;
  refreshing = true;
  window.location.reload();
}

const { needRefresh, updateServiceWorker } = useRegisterSW({
  immediate: true,
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;
    registrationRef = registration;
    checkForSwUpdate();
    updateIntervalId = window.setInterval(checkForSwUpdate, UPDATE_CHECK_MS);
  },
});

function onBeforeInstallPrompt(e: Event) {
  e.preventDefault();
  deferredPrompt.value = e as BeforeInstallPromptEvent;
  if (!dismissedInstall.value) {
    showInstall.value = true;
  }
}

async function installApp() {
  if (!deferredPrompt.value) return;
  await deferredPrompt.value.prompt();
  await deferredPrompt.value.userChoice;
  deferredPrompt.value = null;
  showInstall.value = false;
}

function dismissInstall() {
  showInstall.value = false;
  dismissedInstall.value = true;
  localStorage.setItem("pwa-install-dismissed", "1");
}

function dismissUpdate() {
  needRefresh.value = false;
}

async function applyUpdate() {
  await updateServiceWorker(true);
}

onMounted(() => {
  window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  document.addEventListener("visibilitychange", onVisibilityChange);
  navigator.serviceWorker?.addEventListener("controllerchange", onControllerChange);
});

onUnmounted(() => {
  window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  document.removeEventListener("visibilitychange", onVisibilityChange);
  navigator.serviceWorker?.removeEventListener("controllerchange", onControllerChange);
  if (updateIntervalId !== undefined) {
    window.clearInterval(updateIntervalId);
  }
});
</script>

<template>
  <div
    v-if="needRefresh || (showInstall && brandLoaded)"
    class="fixed inset-x-0 bottom-0 z-[100] flex justify-center p-3 pointer-events-none"
  >
    <div
      class="pointer-events-auto flex max-w-md w-full items-start gap-3 rounded-xl bg-slate-900 text-white shadow-lg px-4 py-3"
      role="status"
    >
      <template v-if="needRefresh">
        <RefreshCw class="w-5 h-5 mt-0.5 shrink-0 text-sky-300" />
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium">{{ t("pwa.update_available") }}</p>
          <p class="text-xs text-slate-300 mt-0.5">{{ t("pwa.update_hint") }}</p>
          <div class="mt-2 flex gap-2">
            <button
              type="button"
              class="text-xs font-semibold bg-sky-500 hover:bg-sky-400 rounded-lg px-3 py-1.5"
              @click="applyUpdate"
            >
              {{ t("pwa.update_now") }}
            </button>
            <button
              type="button"
              class="text-xs text-slate-300 hover:text-white px-2 py-1.5"
              @click="dismissUpdate"
            >
              {{ t("common.close") }}
            </button>
          </div>
        </div>
      </template>

      <template v-else>
        <Download class="w-5 h-5 mt-0.5 shrink-0 text-sky-300" />
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium">{{ t("pwa.install_title", { app: appName }) }}</p>
          <p class="text-xs text-slate-300 mt-0.5">{{ t("pwa.install_hint") }}</p>
          <div class="mt-2 flex gap-2">
            <button
              type="button"
              class="text-xs font-semibold bg-sky-500 hover:bg-sky-400 rounded-lg px-3 py-1.5"
              @click="installApp"
            >
              {{ t("pwa.install") }}
            </button>
            <button
              type="button"
              class="text-xs text-slate-300 hover:text-white px-2 py-1.5"
              @click="dismissInstall"
            >
              {{ t("common.close") }}
            </button>
          </div>
        </div>
        <button
          type="button"
          class="text-slate-400 hover:text-white shrink-0"
          :aria-label="t('common.close')"
          @click="dismissInstall"
        >
          <X class="w-4 h-4" />
        </button>
      </template>
    </div>
  </div>
</template>
